import type { AppointingOrderType } from './api/newClient.ts';

/**
 * What intake's appointing-order list offers (Arthur, 23 October, A-#94 to A-#96): every appointing type, and one
 * choice that is not a type of its own. An order appointing a TEMPORARY successor guardian is the successor code
 * with is_temporary true: generation reads the order's is_temporary, never its code, so it owes exactly what a
 * temporary order owes (A-#86: the commission at 90 days, no oath, no initial or annual report), and the client is
 * AIP. POST /clients takes is_temporary as sent (routes/clients.ts), so this is where the term is decided.
 */
export type IntakeOrderChoice = AppointingOrderType | 'TEMPORARY_SUCCESSOR';

export const TEMPORARY_SUCCESSOR_LABEL = 'Order appointing a temporary successor guardian';

/** The list, in order: the temporary successor directly after the successor guardian order. */
export function intakeChoices(types: readonly AppointingOrderType[]): IntakeOrderChoice[] {
  return types.flatMap((t): IntakeOrderChoice[] => (t === 'SUCCESSOR_GUARDIAN_APPOINTED' ? [t, 'TEMPORARY_SUCCESSOR'] : [t]));
}

/** The code and the term a choice is sent as. */
export function choiceOrder(c: IntakeOrderChoice): { code: AppointingOrderType; temporary: boolean } {
  if (c === 'TEMPORARY_SUCCESSOR') return { code: 'SUCCESSOR_GUARDIAN_APPOINTED', temporary: true };
  return { code: c, temporary: c === 'TEMPORARY_GUARDIAN_APPOINTED' };
}

/** What the order makes them, in Arthur's words (A-#95), after "This order makes them AIP|IP". */
export function orderHint(c: IntakeOrderChoice): string {
  switch (c) {
    case 'TEMPORARY_GUARDIAN_APPOINTED': return ' — a temporary appointment; the court has not yet made a permanent one.';
    case 'TEMPORARY_SUCCESSOR': return ' — a temporary appointment of the agency in place of a previous guardian.';
    case 'SUCCESSOR_GUARDIAN_APPOINTED': return ' — a permanent appointment of the agency in place of a previous guardian.';
    default: return ' — it follows a finding that the client lacks capacity.';
  }
}
